import { useRef, useEffect, useLayoutEffect, useState } from "react";
import { ArrowUpRight, Compass, Scale, Sparkles, Bookmark, Database, LineChart, Bot, ImageIcon, CreditCard, LayoutDashboard, Check } from "lucide-react";
import { useInView, animate } from "framer-motion";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { DeviceFrameDesktop, DeviceFramePhone } from "@/components/DeviceFrames";
import { useLang } from "@/i18n/translations";
import { gsap, ScrollTrigger, usePrefersReducedMotion } from "@/hooks/useSmoothScroll";
import { DA2, PROJECTS } from "@/lib/siteConfig";

const DA = PROJECTS.find((p) => p.id === "dealalert");
const FLOW_ICONS = [Compass, Scale, Sparkles, Bookmark];
const FLOW_KEYS = ["discover", "evaluate", "askAi", "saveAct"];
const flowMedia = FLOW_KEYS.map((k) => DA2.flow[k]);

const useIsDesktop = () => {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.innerWidth >= 1024);
  useEffect(() => {
    const fn = () => setD(window.innerWidth >= 1024);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return d;
};

// img with intrinsic dimensions — prevents lazy-load layout shift
const Shot = ({ asset, eager = false, className = "" }) => (
  <img
    src={asset.src}
    alt={asset.alt}
    width={asset.w}
    height={asset.h}
    loading={eager ? "eager" : "lazy"}
    className={`block w-full h-auto ${className}`}
  />
);

// Lazy autoplay video: loads when visible, plays only while `active` + visible.
const LazyVideo = ({ asset, active = true, className = "", testId }) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const v = ref.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([e]) => setVisible(e.isIntersecting),
      { threshold: 0.2 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (visible && active) {
      if (v.preload === "none") v.preload = "auto";
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, [visible, active]);
  return (
    <video
      ref={ref}
      data-testid={testId}
      poster={asset.poster}
      width={asset.w}
      height={asset.h}
      muted
      loop
      playsInline
      preload="none"
      className={className || "block w-full h-auto"}
    >
      <source src={asset.src} type="video/mp4" />
      {asset.webm && <source src={asset.webm} type="video/webm" />}
    </video>
  );
};

const Kicker = ({ children }) => (
  <p className="flex items-center gap-2 text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">
    <span className="w-1.5 h-1.5 rounded-full bg-[#00d4ff]" style={{ boxShadow: "0 0 12px rgba(0,212,255,0.7)" }} aria-hidden="true" />
    {children}
  </p>
);

const StepBadge = ({ index }) => {
  const Icon = FLOW_ICONS[index];
  return (
    <span
      className="flex items-center justify-center w-11 h-11 rounded-xl border border-[#00d4ff]/30 bg-[#00d4ff]/10 text-[#00d4ff]"
      style={{ boxShadow: "0 0 22px rgba(0,212,255,0.16)" }}
    >
      <Icon className="w-5 h-5" aria-hidden="true" />
    </span>
  );
};

const CountUp = ({ value, suffix = "" }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const reduced = usePrefersReducedMotion();
  const [display, setDisplay] = useState(reduced ? value : 0);
  useEffect(() => {
    if (!inView || reduced) return undefined;
    const controls = animate(0, value, {
      duration: 1.8,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(Math.round(v)),
    });
    return () => controls.stop();
  }, [inView, value, reduced]);
  return (
    <span ref={ref}>
      {display.toLocaleString("de-AT")}
      {suffix}
    </span>
  );
};

/* ============ Hero — Desktop + Mobile mockup with subtle parallax ============ */
const Hero = () => {
  const { t } = useLang();
  const reduced = usePrefersReducedMotion();
  const wrapRef = useRef(null);
  const deskRef = useRef(null);
  const phoneRef = useRef(null);

  useLayoutEffect(() => {
    if (reduced) return undefined;
    const ctx = gsap.context(() => {
      gsap.fromTo(deskRef.current, { y: 18 }, { y: -18, ease: "none", scrollTrigger: { trigger: wrapRef.current, start: "top bottom", end: "bottom top", scrub: 1 } });
      gsap.fromTo(phoneRef.current, { y: 46 }, { y: -30, ease: "none", scrollTrigger: { trigger: wrapRef.current, start: "top bottom", end: "bottom top", scrub: 1 } });
    }, wrapRef);
    return () => ctx.revert();
  }, [reduced]);

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
      <Reveal>
        <div className="flex items-center gap-4">
          <img src={DA2.logo} alt="DealAlert Logo" width="513" height="513" className="w-12 h-12 rounded-xl border border-white/10" loading="eager" />
          <div>
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">{t.dealalert.kicker}</p>
            <p className="font-display text-lg font-semibold text-foreground">DealAlert.ai</p>
          </div>
        </div>
        <h1 className="mt-8 font-display text-3xl sm:text-4xl lg:text-[3.25rem] font-semibold tracking-[-0.025em] leading-[1.05] text-foreground max-w-3xl">
          {t.dealalert.headline}
        </h1>
        <p className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl">{t.dealalert.subline}</p>
        <div className="mt-6 flex flex-wrap gap-2">
          {DA.allBadges.map((b) => (
            <Badge key={b} variant="outline" className="bg-white/5 border-white/10 text-foreground/80 font-mono-cs text-[10px] font-normal rounded-full px-2.5">
              {b}
            </Badge>
          ))}
        </div>
        <a
          data-testid="dealalert-hero-visit"
          href={DA.url}
          target="_blank"
          rel="noopener noreferrer"
          className="mt-7 inline-flex items-center gap-2 text-sm font-medium text-[#00d4ff] hover:text-[#33ddff] transition-colors duration-200"
        >
          {t.dealalert.visit} <ArrowUpRight className="w-4 h-4" />
        </a>
      </Reveal>

      <div ref={wrapRef} className="relative mt-14 [perspective:1400px]">
        <div
          aria-hidden="true"
          className="absolute -top-16 left-1/2 -translate-x-1/2 w-[90%] max-w-[680px] h-[420px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(0,212,255,0.10), transparent 70%)" }}
        />
        <Reveal delay={0.1}>
          <div ref={deskRef} className="relative will-change-transform">
            <DeviceFrameDesktop url="dealalert.ai" testId="dealalert-hero-desktop">
              <Shot asset={DA2.hero} eager />
            </DeviceFrameDesktop>
          </div>
          <div ref={phoneRef} className="absolute -bottom-10 right-0 sm:right-8 w-[26%] max-w-[180px] will-change-transform">
            <DeviceFramePhone testId="dealalert-hero-phone">
              <Shot asset={DA2.heroPhone} />
            </DeviceFramePhone>
          </div>
        </Reveal>
      </div>
    </div>
  );
};

/* ============ Problem / Solution — split with point list (phone overlays IMAGE only, lg) ============ */
const SplitBlock = ({ kicker, title, text, points, asset, phone, reverse = false, testId }) => (
  <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10" data-testid={testId}>
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
      <Reveal className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
        <Kicker>{kicker}</Kicker>
        <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-foreground leading-[1.12]">{title}</h2>
        <p className="mt-4 text-base text-muted-foreground leading-relaxed">{text}</p>
        <ul className="mt-6 space-y-3">
          {points.map((p) => (
            <li key={p} className="flex items-start gap-3 text-sm text-foreground/85">
              <Check className="w-4 h-4 mt-0.5 shrink-0 text-[#00d4ff]" aria-hidden="true" />
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </Reveal>
      <Reveal delay={0.12} className={`lg:col-span-7 ${reverse ? "lg:order-1" : ""}`}>
        <div className="relative">
          <DeviceFrameDesktop url="dealalert.ai">
            <Shot asset={asset} />
          </DeviceFrameDesktop>
          {phone && (
            <div className="hidden lg:block absolute bottom-4 right-4 w-[20%] max-w-[140px]">
              <DeviceFramePhone>
                <Shot asset={phone} />
              </DeviceFramePhone>
            </div>
          )}
        </div>
      </Reveal>
    </div>
  </div>
);

/* ============ Product Flow — cinematic pinned scrollytelling (desktop) ============ */
/* Text column scrolls and drives the active step; the media stage stays sticky
   in the viewport, screens crossfade, a phone shows the matching mobile view,
   and both drift at different speeds (parallax). No phone ever covers text —
   text lives in the left column, media in the right sticky stage. */
const CinematicFlowPinned = () => {
  const { t } = useLang();
  const containerRef = useRef(null);
  const deskWrapRef = useRef(null);
  const phoneWrapRef = useRef(null);
  const beatRefs = useRef([]);
  const [active, setActive] = useState(0);
  const steps = t.dealalert.flow.steps;

  // active step driven by the scrolling text beats
  useEffect(() => {
    const triggers = beatRefs.current.map((el, i) =>
      ScrollTrigger.create({
        trigger: el,
        start: "top 55%",
        end: "bottom 55%",
        onEnter: () => setActive(i),
        onEnterBack: () => setActive(i),
      })
    );
    return () => triggers.forEach((tr) => tr.kill());
  }, []);

  // depth parallax inside the sticky stage (no pin)
  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(deskWrapRef.current, { y: 20 }, { y: -20, ease: "none", scrollTrigger: { trigger: containerRef.current, start: "top bottom", end: "bottom top", scrub: 1.1 } });
      gsap.fromTo(phoneWrapRef.current, { y: 48 }, { y: -34, ease: "none", scrollTrigger: { trigger: containerRef.current, start: "top bottom", end: "bottom top", scrub: 1.1 } });
    }, containerRef);
    return () => ctx.revert();
  }, []);

  return (
    <div ref={containerRef} className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
      <div className="grid grid-cols-12 gap-10">
        {/* Text beats (scroll) */}
        <div className="col-span-5">
          {steps.map((step, i) => (
            <div
              key={step.num}
              ref={(el) => (beatRefs.current[i] = el)}
              data-testid={`dealalert-flow-step-${i + 1}`}
              className={`min-h-[78vh] flex flex-col justify-center transition-opacity duration-500 ${active === i ? "opacity-100" : "opacity-30"}`}
            >
              <div className="flex items-center gap-3">
                <StepBadge index={i} />
                <span className="font-mono-cs text-[11px] uppercase tracking-[0.22em] text-[#00d4ff]">{step.num} · {step.label}</span>
              </div>
              <h3 className="mt-5 font-display text-2xl xl:text-3xl font-semibold tracking-[-0.02em] text-foreground leading-[1.12]">{step.title}</h3>
              <p className="mt-3 text-base text-muted-foreground leading-relaxed max-w-md">{step.text}</p>
            </div>
          ))}
        </div>

        {/* Sticky media stage */}
        <div className="col-span-7">
          <div className="sticky top-0 h-screen flex flex-col items-center justify-center">
            <div className="relative w-full [perspective:1400px]">
              <div className="cs-stage-glow" key={active} aria-hidden="true" />
              <div ref={deskWrapRef} className="relative will-change-transform">
                <DeviceFrameDesktop url="dealalert.ai" testId="dealalert-flow-desktop">
                  <div className="relative bg-[#0a0e16]" style={{ aspectRatio: "1600/905" }}>
                    {flowMedia.map((m, i) => (
                      <img
                        key={FLOW_KEYS[i]}
                        src={m.desktop.src}
                        alt={m.desktop.alt}
                        width={m.desktop.w}
                        height={m.desktop.h}
                        loading={i === 0 ? "eager" : "lazy"}
                        className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-700 ${active === i ? "opacity-100" : "opacity-0"}`}
                      />
                    ))}
                  </div>
                </DeviceFrameDesktop>

                {/* Phone overlay — picture-in-picture over the desktop image */}
                <div ref={phoneWrapRef} className="absolute bottom-3 right-3 sm:right-5 w-[24%] max-w-[168px] will-change-transform">
                  <DeviceFramePhone testId="dealalert-flow-phone">
                    <div className="relative bg-[#05070d]" style={{ aspectRatio: "860/1861" }}>
                      {flowMedia.map((m, i) =>
                        m.phoneVideo ? (
                          <LazyVideo
                            key={FLOW_KEYS[i]}
                            asset={m.phoneVideo}
                            active={active === i}
                            testId="dealalert-swipe-video"
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${active === i ? "opacity-100" : "opacity-0"}`}
                          />
                        ) : (
                          <img
                            key={FLOW_KEYS[i]}
                            src={m.phone.src}
                            alt={m.phone.alt}
                            width={m.phone.w}
                            height={m.phone.h}
                            loading="lazy"
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${active === i ? "opacity-100" : "opacity-0"}`}
                          />
                        )
                      )}
                    </div>
                  </DeviceFramePhone>
                </div>
              </div>

              {/* Progress indicator */}
              <div className="mt-12 flex items-center justify-center gap-2.5" data-testid="dealalert-flow-progress" aria-hidden="true">
                {steps.map((s, i) => (
                  <span key={s.num} className={`h-1.5 rounded-full transition-all duration-500 ${active === i ? "w-8 bg-[#00d4ff]" : "w-1.5 bg-white/15"}`} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Stacked fallback (mobile + reduced motion): phone sits BELOW the desktop, never over text.
const CinematicFlowStacked = () => {
  const { t } = useLang();
  const steps = t.dealalert.flow.steps;
  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10 space-y-24">
      {steps.map((step, i) => {
        const m = flowMedia[i];
        return (
          <Reveal key={step.num} data-testid={`dealalert-flow-step-${i + 1}`}>
            <div className="flex items-center gap-3">
              <StepBadge index={i} />
              <span className="font-mono-cs text-[11px] uppercase tracking-[0.22em] text-[#00d4ff]">{step.num} · {step.label}</span>
            </div>
            <h3 className="mt-5 font-display text-2xl font-semibold tracking-[-0.02em] text-foreground leading-[1.12]">{step.title}</h3>
            <p className="mt-3 text-base text-muted-foreground leading-relaxed">{step.text}</p>
            <div className="mt-7">
              <DeviceFrameDesktop url="dealalert.ai" testId={`dealalert-flow-desktop-${i + 1}`}>
                <Shot asset={m.desktop} eager={i === 0} />
              </DeviceFrameDesktop>
              <div className="mt-6 flex justify-center">
                <div className="w-[56%] max-w-[210px]">
                  <DeviceFramePhone testId={`dealalert-flow-phone-${i + 1}`}>
                    {m.phoneVideo ? (
                      <LazyVideo asset={m.phoneVideo} testId="dealalert-swipe-video" />
                    ) : (
                      <Shot asset={m.phone} />
                    )}
                  </DeviceFramePhone>
                  {m.phoneVideo && <p className="mt-3 text-center text-[10px] font-mono-cs text-muted-foreground/70">{t.dealalert.videoNote}</p>}
                </div>
              </div>
            </div>
          </Reveal>
        );
      })}
    </div>
  );
};

const CinematicFlow = () => {
  const reduced = usePrefersReducedMotion();
  const isDesktop = useIsDesktop();
  return isDesktop && !reduced ? <CinematicFlowPinned /> : <CinematicFlowStacked />;
};

const TECH_ICONS = [Database, LineChart, Bot, ImageIcon, CreditCard, LayoutDashboard];

/* ============ Main ============ */
export const DealAlertCaseStudy = () => {
  const { t } = useLang();

  return (
    <section
      id="case-dealalert"
      data-testid="case-study-dealalert"
      className="relative bg-[#070a12] border-y border-white/5 py-20 sm:py-24"
    >
      {/* decorative glows clipped in their own layer so the section keeps NO overflow
          (CSS position:sticky in the cinematic flow needs no overflow-hidden ancestor) */}
      <div aria-hidden="true" className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-[6%] right-[-6%] w-[640px] h-[640px] rounded-full"
          style={{ background: "radial-gradient(50% 50% at 50% 50%, rgba(0,212,255,0.08), transparent 70%)" }}
        />
      </div>

      {/* 1 — HERO */}
      <Hero />

      {/* 2 — PROBLEM */}
      <div className="mt-28 sm:mt-36">
        <SplitBlock
          testId="dealalert-problem"
          kicker={t.dealalert.problem.kicker}
          title={t.dealalert.problem.title}
          text={t.dealalert.problem.text}
          points={t.dealalert.problem.points}
          asset={DA2.problem}
        />
      </div>

      {/* 3 — SOLUTION */}
      <div className="mt-28 sm:mt-36">
        <SplitBlock
          testId="dealalert-solution"
          kicker={t.dealalert.solution.kicker}
          title={t.dealalert.solution.title}
          text={t.dealalert.solution.text}
          points={t.dealalert.solution.points}
          asset={DA2.solution}
          phone={DA2.solutionPhone}
          reverse
        />
      </div>

      {/* 4 — PRODUCT FLOW (cinematic) */}
      <div className="mt-32 sm:mt-40" data-testid="dealalert-flow">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Kicker>{t.dealalert.flow.kicker}</Kicker>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-[-0.02em] leading-[1.08] text-foreground">
              {t.dealalert.flow.title}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">{t.dealalert.flow.subtitle}</p>
          </Reveal>
        </div>
        <div className="mt-10">
          <CinematicFlow />
        </div>
      </div>

      {/* 5 — TECH / SYSTEM BEHIND */}
      <div className="mt-32 sm:mt-40" data-testid="dealalert-system">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <Reveal className="max-w-2xl">
            <Kicker>{t.dealalert.tech.kicker}</Kicker>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold tracking-[-0.02em] leading-[1.08] text-foreground">
              {t.dealalert.tech.title}
            </h2>
            <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">{t.dealalert.tech.text}</p>
          </Reveal>

          <Reveal delay={0.1} className="mt-12">
            <DeviceFrameDesktop url="dealalert.ai" testId="dealalert-system-video-frame">
              <LazyVideo asset={DA2.system.video} testId="dealalert-system-video" />
            </DeviceFrameDesktop>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dealalert-tech-grid">
            {t.dealalert.tech.items.map((item, i) => {
              const Icon = TECH_ICONS[i % TECH_ICONS.length];
              return (
                <Reveal key={item.name} delay={i * 0.06}>
                  <div className="h-full rounded-2xl bg-white/[0.03] border border-white/10 p-6 transition-[border-color,background-color] duration-300 hover:border-[#00d4ff]/30 hover:bg-white/[0.05]">
                    <span className="flex items-center justify-center w-10 h-10 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff]">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                    </span>
                    <p className="mt-4 font-display text-base font-semibold text-foreground">{item.name}</p>
                    <p className="mt-1.5 text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>

      {/* 6 — BUSINESS VALUE */}
      <div className="mt-32 sm:mt-40" data-testid="dealalert-value">
        <div className="max-w-[1200px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
            <Reveal className="lg:col-span-6">
              <Kicker>{t.dealalert.value.kicker}</Kicker>
              <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.02em] text-foreground leading-[1.12]">
                {t.dealalert.value.title}
              </h2>
              <p className="mt-4 text-base text-muted-foreground leading-relaxed">{t.dealalert.value.text}</p>
              <div className="mt-8 grid grid-cols-2 gap-4" data-testid="dealalert-stats">
                {t.dealalert.stats.map((s) => (
                  <div key={s.label} className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
                    <p className="font-display text-3xl sm:text-4xl font-semibold text-[#00d4ff]">
                      <CountUp value={s.value} suffix={s.suffix} />
                    </p>
                    <p className="mt-2 text-xs font-mono-cs text-muted-foreground">{s.label}</p>
                  </div>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.12} className="lg:col-span-6">
              <DeviceFrameDesktop url="dealalert.ai" testId="dealalert-pricing-frame">
                <Shot asset={DA2.value.pricing} />
              </DeviceFrameDesktop>
            </Reveal>
          </div>
        </div>
      </div>

      {/* 7 — FINAL CTA (recap band) */}
      <div className="mt-32 sm:mt-40" data-testid="dealalert-final-cta">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10">
          <div className="relative rounded-3xl border border-[#00d4ff]/20 bg-gradient-to-b from-white/[0.05] to-white/[0.02] p-10 sm:p-14 text-center overflow-hidden">
            <div
              aria-hidden="true"
              className="absolute inset-x-0 -top-24 h-48 pointer-events-none"
              style={{ background: "radial-gradient(40% 100% at 50% 0%, rgba(0,212,255,0.18), transparent 70%)" }}
            />
            <p className="text-[11px] font-mono-cs uppercase tracking-[0.22em] text-[#00d4ff]">{t.dealalert.finalCta.kicker}</p>
            <h2 className="mt-4 font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-[-0.025em] text-foreground leading-[1.1] max-w-2xl mx-auto">
              {t.dealalert.finalCta.title}
            </h2>
            <p className="mt-4 text-base text-muted-foreground leading-relaxed max-w-xl mx-auto">{t.dealalert.finalCta.text}</p>
            <a
              data-testid="dealalert-final-visit"
              href={DA.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-[#00d4ff] text-[#05070d] hover:bg-[#33ddff] font-medium text-sm px-7 h-11 shadow-[0_0_28px_rgba(0,212,255,0.22)] transition-[background-color,box-shadow,transform] duration-200 hover:-translate-y-px"
            >
              {t.dealalert.finalCta.primary} <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};
